/**
 * The platform booking lead time: how much notice a new booking needs.
 *
 * This is the client-side mirror of listing_engine's
 * `Core.Listing.BookingLeadTime`, which is the canonical owner of the rule.
 * Keep the two in step; the Elixir module is the source of truth.
 *
 * Why the web needs its own copy: `Core.Listing.Reads.Queries.scope_by_lead_time`
 * drops every row when the check-in date sits inside the platform floor, since
 * no listing on the platform can be booked for it. A dated search using such a
 * date therefore returns zero listings, and search has no listing left to read
 * an availability calendar from. The floor is not exposed over GraphQL, so the
 * only way search can tell "your dates are too soon" apart from "we have no
 * inventory" is to know the floor.
 *
 * Everything here derives from UTC, because the server compares the submitted
 * check-in date against `DateTime.utc_now()`. Deriving from local time would put
 * the boundary a day off for anyone whose local date differs from the UTC date.
 */

import type { Moment } from 'moment';

import dayjs from '@utils/dayjs-setup';

/**
 * The minimum booking notice the platform enforces for every listing, in
 * calendar days, matching `Core.Listing.BookingLeadTime.floor_days/0`.
 *
 * At 2 the day after tomorrow is the first bookable check-in, so same-day and
 * next-day bookings are refused. A listing may demand more through its own
 * `booking_lead_time_hours`, never less, so this floor is the widest window in
 * which a check-in date is guaranteed unbookable across the whole platform,
 * which is the only window search can act on: the per-listing hours are not
 * exposed over GraphQL either.
 */
export const PLATFORM_BOOKING_LEAD_TIME_DAYS = 2;

/**
 * The earliest check-in date any listing on the platform can be booked for,
 * as `YYYY-MM-DD`.
 *
 * Mirrors `Core.Listing.BookingLeadTime.platform_first_bookable_check_in_on/1`:
 * the floor is a calendar-day rule, so the answer is today's UTC date plus the
 * floor and it holds for the whole UTC day rather than sliding with the clock.
 *
 * Written as explicit `getUTC*` arithmetic rather than through dayjs so there
 * is no local-time code path to get wrong. A local derivation would put the
 * boundary a day early for every guest whose local date is behind the UTC
 * date, which is most of the Americas for part of each day, and the server
 * would then reject a date the picker had just offered. Jest resolves its
 * timezone as UTC no matter what `process.env.TZ` says inside the test vm, so
 * that mistake is not something a test on this runner could catch.
 */
export function firstBookableCheckInDate(now: Date = new Date()): string {
  // Date.UTC rolls a day past the end of the month over on its own.
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + PLATFORM_BOOKING_LEAD_TIME_DAYS
    )
  )
    .toISOString()
    .slice(0, 10);
}

/**
 * Whether a check-in date sits inside the platform booking lead time, which
 * means the backend excludes every listing from a dated search using it.
 *
 * Takes and compares `YYYY-MM-DD` strings so the value under test is the same
 * string the search sends to GraphQL, rather than a re-parsed local timestamp.
 */
export function isCheckInTooSoon(
  checkInDate: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!checkInDate) return false;

  return checkInDate < firstBookableCheckInDate(now);
}

/**
 * The `isOutsideRange` predicate every search date picker passes by reference.
 *
 * It lives here rather than as an inline arrow in each of the five pickers so
 * there is one predicate to test behaviourally, and so a picker either passes
 * this function or visibly does not. It also subsumes the
 * `!isInclusivelyAfterDay(day, moment())` rule the pickers used to carry: the
 * floor refuses today and tomorrow, so every past day is refused with them.
 *
 * The pickers leave `minDate` as null on purpose: in react-dates'
 * `DayPickerRangeController`, `minDate` only decides whether month navigation
 * is disabled and never blocks a day, so `isOutsideRange` is the only gate
 * that keeps a too-soon date unselectable.
 *
 * The day is formatted in local time deliberately: that is the calendar date
 * the guest sees on the cell and the string the search would send, so it is
 * the value the boundary should be applied to.
 */
export function isSearchDayOutsideRange(day: Moment): boolean {
  return isCheckInTooSoon(day.format('YYYY-MM-DD'));
}

/**
 * Whether a search's dates are the reason it came back empty.
 *
 * Both dates are required, because `Core.Listing.Reads.Queries.scope_by_stay/3`
 * only matches when `check_in_date` and `check_out_date` are both non-nil and
 * `scope_by_lead_time` sits inside that clause. Measured against the live
 * graph: a too-soon check-in paired with a check-out returned 0 listings while
 * the same check-in with no check-out returned all 7,617, the same as an
 * undated search. So a check-in-only search that comes back empty was emptied
 * by something else, and blaming the dates there would be a new wrong
 * explanation in place of the one this fix removes.
 */
export function isDatedSearchTooSoon(
  checkInDate: string | null | undefined,
  checkOutDate: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!checkOutDate) return false;

  return isCheckInTooSoon(checkInDate, now);
}

/**
 * The earliest bookable check-in date in guest-facing form, matching the
 * `Calendar.strftime(date, "%B %-d, %Y")` the backend uses.
 */
export function formatBookableCheckInDate(date: string): string {
  return dayjs(date).format('MMMM D, YYYY');
}
